/// <amd-module name="Wit/SprintsHub/SprintsView/Components/SprintHeader/SprintEditDatesDialog" />

import * as React from "react";
import { getTFSPageData } from "Tfs/Platform/Page";
import { IObservableValue, ObservableValue } from "VSS/Core/Observable";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssHistoryService } from "VSS/Platform/History";
import { IVssComponentContext, IVssLayoutManager, VssComponent } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { DatePicker } from "VSSUI/DatePicker";
import { Dialog } from "VSSUI/Dialog";
import { FormItem } from "VSSUI/FormItem";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { TextField } from "VSSUI/TextField";
import { Tooltip } from "VSSUI/TooltipEx";
import { TreeStructureGroup, WorkItemClassificationNode } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { getWorkItemTrackingClient } from "Wit/Clients/Wit/RestClient/WorkItemTracking";
import { shiftToUTC } from "Wit/Common/DateUtils";
import * as Resources from "Wit/SprintsHub/SprintsView/Resources";
import { FormDirtyDialog } from "./FormDirtyDialog";

export interface ISprintEditDatesDialogProps {
    endDate?: Date;
    iterationGuid?: string;
    iterationId?: number;
    iterationName?: string;
    iterationPath?: string;
    startDate?: Date;
    teamName: string;
    onDismiss: () => void;
    onSprintDetailsChanged: (node: WorkItemClassificationNode) => void;
}

interface IApiClassificationPermissionsDataProviderResponse {
    data: {
        hasPermission: boolean;
        errorMessage?: string;
    };
}

const forwardSlashRegex = /\//g;
const ApiClassificationPermissionsDataProvider = "ms.vss-work-web.api-classification-permissions-data-provider";

export class SprintEditDatesDialog extends VssComponent<ISprintEditDatesDialogProps> {
    private dialogErrorMessage: IObservableValue<string | undefined> = new ObservableValue(undefined);
    private dialogErrorMessageDetails: IObservableValue<string | undefined> = new ObservableValue(undefined);
    private expandErrorDetails: IObservableValue<boolean> = new ObservableValue(false);
    private hasNodeEditPermission: IObservableValue<boolean> = new ObservableValue(false);
    private isFormDirty: IObservableValue<boolean> = new ObservableValue(false);
    private layoutManager: IVssLayoutManager;

    private name: IObservableValue<string> = new ObservableValue("");
    private nameValidationMessage: IObservableValue<string | undefined> = new ObservableValue(undefined);
    private nameValidationError: IObservableValue<boolean> = new ObservableValue(false);
    private startDate: IObservableValue<Date | undefined> = new ObservableValue(undefined);
    private endDate: IObservableValue<Date | undefined> = new ObservableValue(undefined);

    constructor(props: ISprintEditDatesDialogProps, context: IVssComponentContext) {
        super(props, context);
        this.layoutManager = context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        this.isFormDirty.value = false;
    }

    public componentDidMount(): void {
        super.componentDidMount();
        if (this.props.iterationName && this.props.iterationName.length > 0) {
            this.name.value = this.props.iterationName;
        }
        if (this.props.startDate && this.props.endDate) {
            this.startDate.value = shiftToUTC(this.props.startDate);
            this.endDate.value = shiftToUTC(this.props.endDate);
        }
        this.checkNodeEditPermssions();
    }

    public componentDidUpdate(): void {
        this.checkNodeEditPermssions();
    }

    public render(): JSX.Element | null {
        return (
            <Observer name={this.name} hasNodeEditPermission={this.hasNodeEditPermission}>
                {(observerProps: { name: string; hasNodeEditPermission: boolean }) => {
                    return (
                        <Dialog
                            lightDismiss={false}
                            onDismiss={() => this.onDismiss()}
                            resizable={true}
                            titleProps={{ text: Resources.EditIterationDialogTitle }}
                            footerButtonProps={[
                                {
                                    text: Resources.SaveAndClose,
                                    onClick: () => this.save(),
                                    primary: true,
                                    disabled: !observerProps.hasNodeEditPermission
                                },
                                {
                                    text: Resources.Cancel,
                                    onClick: () => this.onDismiss()
                                }
                            ]}
                        >
                            <Tooltip overflowOnly={true}>
                                <span className="text-ellipsis title-m">{observerProps.name}</span>
                            </Tooltip>
                            <Observer errorMessage={this.dialogErrorMessage} errorMessageDetails={this.dialogErrorMessageDetails} expandErrorDetails={this.expandErrorDetails}>
                                {(props: { errorMessage: string | undefined; errorMessageDetails: string | undefined; expandErrorDetails: boolean }) => {
                                    return (
                                        <>
                                            {props.errorMessage &&
                                                <MessageCard severity={MessageCardSeverity.Error} onDismiss={() => (this.dialogErrorMessage.value = undefined)}>
                                                    {props.errorMessage}
                                                    {props.errorMessageDetails &&
                                                        <>
                                                            <Button
                                                                className="error-details-button"
                                                                subtle={true}
                                                                text={props.expandErrorDetails ? Resources.HideDetails : Resources.ShowDetails}
                                                                onClick={() => {
                                                                    this.expandErrorDetails.value = !this.expandErrorDetails.value;
                                                                }}
                                                            />
                                                            {props.expandErrorDetails &&
                                                                <div> {props.errorMessageDetails} </div>}
                                                        </>}
                                                </MessageCard>}
                                        </>
                                    );
                                }}
                            </Observer>
                            <FormItem
                                className="padding-vertical-8"
                                label={Resources.IterationName}
                                message={this.nameValidationMessage}
                                error={this.nameValidationError}
                            >
                                <TextField
                                    value={observerProps.name}
                                    onChange={(event, value) => {
                                        this.name.value = value;
                                        this.isFormDirty.value = true;
                                        if (value) {
                                            this.nameValidationError.value = false;
                                            this.nameValidationMessage.value = "";
                                        }
                                        else {
                                            this.nameValidationError.value = true;
                                            this.nameValidationMessage.value = "You must specify a name for this iteration.";
                                        }
                                    }}
                                    ref={ref => {
                                            ref?.focus();
                                    }}
                                    required={true}
                                />
                            </FormItem>
                            <FormItem className="padding-vertical-8" label={Resources.StartDate}>
                                <DatePicker
                                    ariaLabel={Resources.StartDate}
                                    value={this.startDate}
                                    onChange={date => {
                                        this.startDate.value = date;
                                        this.isFormDirty.value = true;
                                    }}
                                />
                            </FormItem>
                            <FormItem className="padding-vertical-8" label={Resources.EndDate}>
                                <DatePicker
                                    ariaLabel={Resources.EndDate}
                                    value={this.endDate}
                                    onChange={date => {
                                        this.endDate.value = date;
                                        this.isFormDirty.value = true;
                                    }}
                                />
                            </FormItem>
                            <FormItem className="padding-vertical-8" label={Resources.Location}>
                                <TextField disabled={true} value={this.getParentLocationPath()} />
                            </FormItem>
                        </Dialog>
                    );
                }}
            </Observer>
        );
    }

    private async checkNodeEditPermssions() {
        if (this.props.iterationGuid) {
            const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
            const permissions = await contributionService.getDataAsync<IApiClassificationPermissionsDataProviderResponse>(
                ApiClassificationPermissionsDataProvider,
                {
                    nodeId: this.props.iterationGuid,
                    genericWriteAccess: true
                },
                true /* ignore cache */
            );
            if (permissions) {
                if (permissions.data.hasPermission) {
                    this.hasNodeEditPermission.value = true;
                }
                else {
                    this.hasNodeEditPermission.value = false;
                    this.dialogErrorMessage.value = Resources.IterationNodeNoEditPermissions;
                    if (permissions.data.errorMessage) {
                        this.dialogErrorMessageDetails.value = permissions.data.errorMessage;
                    }
                }
            }
            else {
                this.dialogErrorMessage.value = Resources.UnexpectedServerError;
            }
        }
    }

    private getParentLocationPath(): string {
        let parentPath = "";
        if (this.props.iterationPath) {
            // transfrom from "ProjectName\Iteration 1\Iteration 1.1" to "ProjectName/Iteration 1"
            const splitArray = this.props.iterationPath.split("\\").filter(x => x);
            parentPath = splitArray.splice(0, splitArray.length - 1).join("/");
        }
        return parentPath;
    }

    private openDialog(callout: (onDismiss: () => void) => JSX.Element): void {
        this.layoutManager.renderCallout(onDismiss => callout(onDismiss));
    }

    private onDismiss() {
        if (this.props.onDismiss) {
            if (this.isFormDirty.value) {
                this.openDialog(onDismiss => {
                    const dismiss = () => {
                        onDismiss();
                    };
                    const dismissDirtyForm = () => {
                        onDismiss();
                        this.props.onDismiss();
                    }
                    return (
                        <FormDirtyDialog
                            dismissDirtyForm={dismissDirtyForm}
                            onDismiss={dismiss}
                        />
                    );
                });
            }
            else {
                this.props.onDismiss();
            }
        }
    }

    private async save() {
        const restClient = getWorkItemTrackingClient(this.context.pageContext);
        if (this.name.value && ((this.startDate.value && this.endDate.value) || (!this.startDate.value && !this.endDate.value))) {
            const iterationNode = this.getPatchNode(this.name.value, this.startDate.value, this.endDate.value);
            const projectId = getTFSPageData(this.context.pageContext)?.project?.id ?? "";
            let iterationPath = "";
            if (this.props.iterationPath) {
                // transfrom from "ProjectName\Iteration 1\Iteration 1.1" to "Iteration 1\Iteration 1.1"
                iterationPath = this.props.iterationPath.substring(this.props.iterationPath.indexOf("\\") + 1);
            }
            else {
                const historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
                const state = historyService.getState();
                iterationPath = state["iteration"].substring(state["iteration"].indexOf("/") + 1).replace(forwardSlashRegex, "\\");
            }
            try {
                const newNode = await restClient.updateClassificationNode(iterationNode, projectId, TreeStructureGroup.Iterations, iterationPath);
                if (newNode) {
                    this.props.onSprintDetailsChanged(newNode);
                    this.isFormDirty.value = false;
                    this.onDismiss();
                }
            }
            catch (error) {
                error = error as Error;
                this.dialogErrorMessage.value = Resources.SaveErrorMessage;
                this.dialogErrorMessageDetails.value = error.message;
            }
        }
        else {
            this.dialogErrorMessage.value = Resources.SaveErrorMessage;
        }
    }

    private getPatchNode(iterationName: string, iterationStartDate?: Date, iterationEndDate?: Date): any {
        if (iterationStartDate && iterationEndDate) {
            return {
                name: iterationName,
                attributes: {
                    startDate: new Date(Date.UTC(iterationStartDate.getFullYear(), iterationStartDate.getMonth(), iterationStartDate.getDate())),
                    finishDate: new Date(Date.UTC(iterationEndDate.getFullYear(), iterationEndDate.getMonth(), iterationEndDate.getDate()))
                }
            };
        }
        else {
            return {
                name: iterationName
            };
        }
    }
}